import React, {useEffect, useRef, useState} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import {KTSVG} from '../../../_metronic/helpers'
import useUtils from './useUtils'
import {useDispatch, useSelector} from 'react-redux'
import formatDate from '../../../_metronic/helpers/formatDate'
import calender from '../../../_metronic/assets/custom-icons/calendar.png'
import {setTitle} from '../../redux/reducers/mainSlice'
import Pagination from '../../modules/pagination/Pagination'
import TableSkeleton from '../../modules/skeleton/TableSkelton'
import DeleteConfirmation from '../../modules/alert/DeleteConfirmation'
import CustomFilterDropdown from '../../modules/common/CustomFilterDropdown'
import Papa, {unparse} from 'papaparse'
import {saveAs} from 'file-saver'
import CSVModal from '../../modules/modal/CSVModal'
import {useHelper} from '../../../_metronic/helpers/useHelper'
import {showAlert} from '../../redux/reducers/alertSlice'

function ListView({className, close}) {
  const {
    fetchSchedules,
    fetchCohort,
    deleteSchedule,
    handleEdit,
    addCSV,
    submitReport,
    fetchAllSchedules,
  } = useUtils()
  const {createSmartChunks} = useHelper()
  const [loading, setLoading] = useState(true)
  const [schedule, setSchedule] = useState()
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [totalPages, setTotalPages] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCohort, setSelectedCohort] = useState(null)

  const [cohortOptions, setCohortOptions] = useState([])

  // Token-based pagination states
  const [cohortNextPageToken, setCohortNextPageToken] = useState(null)

  // Loading states
  const [isLoadingCohorts, setIsLoadingCohorts] = useState(false)

  const [modalVisible, setModalVisible] = useState(false)
  const [selectedId, setSelectedId] = useState(null)

  // CSV functionality states
  const [uploadLoading, setUploadLoading] = useState(false)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [csvData, setcsvData] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [isUpload, setIsUpload] = useState(false)

  const previousSearchTerm = useRef('')

  const navigate = useNavigate()
  const dispatch = useDispatch()
  dispatch(setTitle('Schedule'))

  const handleDelete = async (id) => {
    setSelectedId(id)
    setModalVisible(true)
  }

  const confirmDelete = async () => {
    if (!selectedId) return
    try {
      setLoading(true)
      const result = await deleteSchedule(selectedId)
      setSchedule(result)
    } catch (error) {
      console.error('Error deleting course:', error)
    } finally {
      setLoading(false)
      setModalVisible(false)
      setSelectedId(null)
    }
  }

  const onEdit = async (id) => {
    const result = await handleEdit(id)
    navigate('/schedulelist/editschedule', {state: {result}})
  }

  const getSchedules = async () => {
    try {
      if (searchTerm === previousSearchTerm.current) {
        setLoading(true)
      }
      const offset = (page - 1) * limit
      const scheduleData = await fetchSchedules(searchTerm, selectedCohort, limit, offset)
      setSchedule(scheduleData)
      setTotalPages(Math.ceil(scheduleData?.count / limit))
      previousSearchTerm.current = searchTerm
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  // Load more function for token-based pagination

  const loadMoreCohorts = async () => {
    if (isLoadingCohorts || !cohortNextPageToken) return

    setIsLoadingCohorts(true)
    try {
      const response = await fetchCohort(cohortNextPageToken)
      if (response) {
        setCohortOptions((prev) => [...prev, ...response.records])
        setCohortNextPageToken(response.nextPageToken)
      }
    } catch (error) {
      console.error('Error loading more cohorts:', error)
    } finally {
      setIsLoadingCohorts(false)
    }
  }

  const fetchInitialData = async () => {
    try {
      const cohortResponse = await fetchCohort()
      if (cohortResponse) {
        setCohortOptions(cohortResponse.records)
        setCohortNextPageToken(cohortResponse.nextPageToken)
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  const handleDownload = async () => {
    try {
      setDownloadLoading(true)
      const data = await fetchAllSchedules(searchTerm, selectedCohort)
      const csv = unparse(data, {header: true})
      const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'})
      saveAs(blob, 'livestream_schedules.csv')
    } catch (error) {
      console.error('Error downloading schedules:', error)
    } finally {
      setDownloadLoading(false)
    }
  }

  const uploadFile = async (data) => {
    let successRecords = 0
    const failedRecords = []
    try {
      const chunks = createSmartChunks(data)
      const uploadPromises = chunks.map((chunk) => addCSV(chunk))
      const response = await Promise.all(uploadPromises)

      response.forEach((response) => {
        if (response.success) {
          successRecords += response.success
        }
        if (response.failed?.length > 0) {
          failedRecords.push(...response.failed) // Aggregate failed records
        }
      })

      const successMessage = successRecords
        ? `${successRecords} livestream schedules uploaded successfully`
        : ''
      const failedMessage = failedRecords.length
        ? `${failedRecords.length} livestream schedules failed to upload`
        : ''
      const combinedMessage = [successMessage, failedMessage].filter(Boolean).join('. ')
      dispatch(showAlert({message: combinedMessage, variant: 'info', timeout: 5000}))

      const finalSummary = {
        recordType: 'Livestream Schedules',
        totalRecords: data.length,
        successRecords: successRecords,
        failedRecords: failedRecords,
      }

      const res = await submitReport(finalSummary)
      if (res) {
        dispatch(
          showAlert({message: 'Report Submitted Successfully', variant: 'info', timeout: 5000})
        )
      }
    } catch (error) {
      console.error('Error uploading schedules:', error)
    } finally {
      setUploadLoading(false)
      getSchedules()
    }
  }

  const handleFileChange = (event) => {
    setUploadLoading(true)
    const file = event.target.files[0]
    if (file) {
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        Papa.parse(file, {
          header: true,
          complete: (result) => {
            // Filter out empty rows and trim whitespace from all values
            const filteredData = result.data
              .filter((row) => Object.values(row).some((value) => value && value.trim() !== ''))
              .map((row) => {
                const trimmedRow = {}
                Object.keys(row).forEach((key) => {
                  trimmedRow[key] = typeof row[key] === 'string' ? row[key].trim() : row[key]
                })
                return trimmedRow
              })
            setIsOpen(true)
            setcsvData(filteredData)
            event.target.value = null
          },
          error: (error) => {
            console.error('Error parsing CSV:', error)
          },
        })
      } else {
        dispatch(showAlert({message: 'Please select a valid CSV file.', variant: 'danger'}))
      }
    }
  }

  useEffect(() => {
    if (csvData) {
      const transformedData = csvData?.map((obj, index) => ({
        id: obj?.id,
        topic: obj?.topic,
        cohortId: obj?.cohort,
        totalParticipantsJoined: obj?.totalParticipantsJoined,
        isForBlackforceMember:
          obj?.isForBlackforceMember === 'true' || obj?.isForBlackforceMember === true,
        zoomMeetingId: obj?.zoomMeetingId,
        zoomStartLink: obj?.zoomStartLink,
        zoomJoinLink: obj?.zoomJoinLink,
        startTime: obj?.startTime ? parseInt(obj?.startTime) : null,
        endTime: obj?.endTime ? parseInt(obj?.endTime) : null,
      }))
      uploadFile(transformedData)
      setcsvData(null)
    }
  }, [isUpload])

  useEffect(() => {
    const delayDebounceFn = setTimeout(
      () => {
        getSchedules()
      },
      searchTerm ? 500 : 0
    )
    return () => clearTimeout(delayDebounceFn)
  }, [searchTerm, limit, selectedCohort, page])

  useEffect(() => {
    fetchInitialData()
  }, [])

  if (loading) return <TableSkeleton />
  return (
    <div className={`card ${className}`}>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start'>
          <span className=' mt-1 text-muted fs-7 '>{schedule?.count} Items</span>
        </h3>

        <div className='d-flex align-items-center justify-content-between w-100'>
          <div className='d-flex align-items-center flex-wrap gap-3'>
            <div className='d-flex align-items-center'>
              <KTSVG
                path='/media/icons/duotune/general/gen021.svg'
                className='svg-icon-3 position-absolute ms-6'
              />
              <input
                type='text'
                data-kt-user-table-filter='search'
                className='form-control fs-7 form-control-solid w-250px h-40px ps-14'
                placeholder='Search Schedules'
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setPage(1)
                }}
              />
            </div>

            <div>
              <CustomFilterDropdown
                options={cohortOptions}
                selectedValue={selectedCohort}
                onChange={setSelectedCohort}
                onScrollEnd={loadMoreCohorts}
                isLoading={isLoadingCohorts}
                placeholder='Select Cohort'
                displayField='title'
              />
            </div>
          </div>
          <div className='card-toolbar'>
            <img
              src={calender}
              className='me-5'
              onClick={() => close()}
              style={{cursor: 'pointer', height: '24px', width: '24px'}}
            />

            {/* CSV Download Button */}
            <button
              onClick={handleDownload}
              disabled={downloadLoading}
              className='btn btn-sm btn-light-primary me-2'
            >
              {downloadLoading ? (
                <span className='spinner-border spinner-border-sm me-2' />
              ) : (
                <KTSVG path='/media/icons/duotune/files/fil003.svg' className='svg-icon-2' />
              )}
              <span className='text-nowrap'>Download CSV</span>
            </button>

            {/* CSV Upload Button */}
            <label className='btn btn-sm btn-light-primary me-2' style={{cursor: 'pointer'}}>
              {uploadLoading ? (
                <span className='spinner-border spinner-border-sm me-2' />
              ) : (
                <KTSVG path='/media/icons/duotune/files/fil010.svg' className='svg-icon-2' />
              )}
              <span className='text-nowrap'>Upload CSV</span>
              <input
                type='file'
                accept='.csv'
                onChange={handleFileChange}
                style={{display: 'none'}}
                disabled={uploadLoading}
              />
            </label>

            <a
              onClick={() => navigate('/schedulelist/addschedule')}
              className='btn btn-sm btn-light-primary'
            >
              <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
              <span className='text-nowrap'> Add Live Stream</span>
            </a>
          </div>
        </div>
      </div>

      {schedule?.count === 0 && (
        <div className='mt-20 mb-20 w-sm-100 w-md-75 mx-auto p-5 py-20'>
          <div className='card-body text-center'>
            <span className='svg-icon svg-icon-2hx svg-icon-info mb-5'>
              <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-2x' />
            </span>
            <h4 className='mb-1'>No Records Found</h4>
            <span>Add more schedule or change the filters</span>
          </div>
        </div>
      )}

      {schedule?.count != 0 && (
        <div className='card-body py-10'>
          <div className='table-responsive'>
            <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
              <thead className='align-top'>
                <tr className='fw-bold text-muted'>
                  <th className='min-w-100px'>ID</th>
                  <th className='min-w-100px min-w-150px text-left'>Live Stream Class</th>
                  <th className='min-w-100px'>Cohort</th>
                  <th className='min-w-100px text-nowrap'>For BlackForce Member</th>
                  <th className='min-w-100px text-nowrap'>Total Participants</th>
                  <th className=' min-w-100px'>Zoom Start Link</th>
                  <th className=' min-w-100px'>Zoom Join Link</th>
                  <th className=' min-w-200px'>Start Time</th>
                  <th className=' min-w-200px'>End Time</th>
                  <th className='text-end'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {schedule?.records &&
                  schedule?.records.map((value, index) => {
                    return (
                      <tr key={index}>
                        <td className='text-nowrap'>
                          <a href='#' className='text-dark fw-bold fs-6'>
                            {value.id}
                          </a>
                        </td>
                        <td className='text-left ps-4'>
                          <a
                            onClick={(e) => {
                              e.preventDefault()
                              window.open(value?.zoomStartLink, '_blank')
                            }}
                            href='#'
                            className='text-dark fw-bold  fs-6'
                          >
                            {value?.topic}
                          </a>
                        </td>
                        <td>
                          <a href='#' className='text-dark fw-bold  fs-6'>
                            {value?.cohort?.title}
                          </a>
                        </td>
                        <td>
                          <a href='#' className='text-dark fw-bold  fs-6'>
                            {value?.isForBlackforceMember ? 'Yes' : 'No'}
                          </a>
                        </td>
                        <td>
                          <a href='#' className='text-dark fw-bold  fs-6'>
                            {value?.totalParticipantsJoined}
                          </a>
                        </td>
                        <td>
                          <a
                            onClick={(e) => {
                              if (
                                Date.now() < value?.endTime &&
                                Date.now() >= value?.startTime - 1800000
                              ) {
                                e.preventDefault()
                                window.open(value?.zoomStartLink, '_blank')
                              }
                            }}
                            className='text-dark fw-bold  fs-7 cursor-pointer'
                          >
                            {value?.zoomStartLink?.slice(0, 20) + '...'}
                          </a>
                        </td>
                        <td>
                          <a
                            onClick={(e) => {
                              if (
                                Date.now() < value?.endTime &&
                                Date.now() >= value?.startTime - 1800000
                              ) {
                                e.preventDefault()
                                window.open(value?.zoomJoinLink, '_blank')
                              }
                            }}
                            className='text-dark fw-bold cursor-pointer fs-7'
                          >
                            {value?.zoomJoinLink?.slice(0, 20) + '...'}
                          </a>
                        </td>
                        <td>
                          <a href='#' className='text-dark d-flex  fw-bold  fs-6'>
                            {formatDate(value?.startTime)}
                          </a>
                        </td>
                        <td>
                          <a href='#' className='text-dark d-flex  fw-bold  fs-6'>
                            {formatDate(value?.endTime)}
                          </a>
                        </td>
                        <td className='d-flex justify-content-end'>
                          {Date.now() < value?.endTime && Date.now() >= value?.startTime - 1800000 && (
                            <a
                              onClick={(e) => {
                                e.preventDefault()
                                window.open(value?.zoomStartLink, '_blank')
                              }}
                              className='btn btn-primary btn-sm me-1 py-3 text-nowrap text-center my-auto'
                            >
                              Start Meeting
                            </a>
                          )}
                          <a
                            onClick={() => onEdit(value.id)}
                            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                          >
                            <KTSVG
                              path='/media/icons/duotune/art/art005.svg'
                              className='svg-icon-3 svg-icon-primary'
                            />
                          </a>
                          <a
                            onClick={() => handleDelete(value.id)}
                            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                          >
                            <KTSVG
                              path='/media/icons/duotune/general/gen027.svg'
                              className='svg-icon-3 text-danger'
                            />
                          </a>
                        </td>
                      </tr>
                    )
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className='d-flex align-items-center justify-content-between pb-5 ps-10 pe-5'>
        <div className='d-flex align-items-center gap-3'>
          <span className='fs-6 text-muted'>Show</span>
          <div>
            <select
              defaultValue={limit}
              className='form-select form-select-solid text-muted select2-hidden-accessible'
              onChange={(e) => {
                setPage(1)
                setLimit(e.target.value)
              }}
            >
              <DeleteConfirmation
                isVisible={modalVisible}
                message='Are you sure you want to delete this Schedule?'
                onConfirm={confirmDelete}
                onCancel={() => setModalVisible(false)}
              />
              <option>10</option>
              <option>20</option>
              <option>50</option>
              <option>100</option>
            </select>
          </div>
        </div>
        <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
      </div>

      {/* CSV Upload Modal */}
      {isOpen && (
        <CSVModal
          show={isOpen}
          handleClose={() => {
            setIsOpen(false)
          }}
          handleCancel={() => {
            setUploadLoading(false)
            setIsOpen(false)
          }}
          csvData={csvData}
          upload={() => setIsUpload((prev) => !prev)}
          optionalFields={['id']}
        />
      )}
    </div>
  )
}

export default ListView
